import { useMemo } from 'react';
import type { ChannelItemStatusRow, OrderCountByChannelRow, SalesByChannelRow } from '@/types/dashboard';
import type { EndpointResponse } from '@/types/endpoints';
import { isRecord } from '@/utils/dataUtils';
import { orgDayRange, type DayKey, type EpochMs } from '@/utils/orgCalendar';
import { useOrgCalendar } from '@/hooks/useOrgCalendar';
import { buildDailySnapshots, buildDailyTotals, type DailyMetricRow, type DailySeries } from './dailySeries';
import { useOrgMetric, type OrgMetricFetcher, type OrgMetricState } from './useOrgMetric';

export const DAILY_WINDOW_DAYS = 30;

type DailyParams = { days: number; timezone: string };
type StatusParams = DailyParams & { subscriptionId: number };

export type DailySeriesState =
  | Exclude<OrgMetricState<never>, { status: 'ready' }>
  | {
      status: 'ready';
      window: DayKey[];
      series: DailySeries[];
      timezone: string;
      requestedAt: EpochMs;
      latestObservedDay: DayKey | null;
    };

const requireArray = (value: unknown): unknown[] => {
  if (!Array.isArray(value)) throw new Error('Unexpected metrics response');
  return value;
};

const channelRow = (value: unknown, metric: 'sales' | 'orders'): DailyMetricRow | null => {
  if (!isRecord(value) || typeof value.channel !== 'string' || typeof value.date !== 'string') {
    throw new Error('Unexpected channel metrics row');
  }
  const amount = metric === 'sales' ? value.total : value.num_orders;
  if (typeof amount !== 'number' || !Number.isFinite(amount)) throw new Error('Unexpected channel metrics value');
  const series = value.channel.trim();
  return series ? { series, date: value.date, value: amount } : null;
};

const statusRow = (value: unknown): DailyMetricRow | null => {
  if (
    !isRecord(value) ||
    typeof value.subscription_id !== 'number' ||
    typeof value.status !== 'string' ||
    typeof value.date !== 'string' ||
    typeof value.total !== 'number' ||
    !Number.isFinite(value.total)
  ) {
    throw new Error('Unexpected item-status metrics row');
  }
  const series = value.status.trim();
  return series ? { series, date: value.date, value: value.total } : null;
};

const normalizeChannelResponse = (
  response: EndpointResponse<SalesByChannelRow[] | OrderCountByChannelRow[]>,
  metric: 'sales' | 'orders'
): EndpointResponse<DailyMetricRow[]> => {
  if (response.isError) return response;
  const rows = requireArray(response.result).flatMap((value) => {
    const row = channelRow(value, metric);
    return row ? [row] : [];
  });
  return { ...response, result: rows };
};

const normalizeStatusResponse = (response: EndpointResponse<ChannelItemStatusRow[]>): EndpointResponse<DailyMetricRow[]> => {
  if (response.isError) return response;
  const rows = requireArray(response.result).flatMap((value) => {
    const row = statusRow(value);
    return row ? [row] : [];
  });
  return { ...response, result: rows };
};

const fetchSales: OrgMetricFetcher<DailyParams, DailyMetricRow[]> = async (endpoints, params, options) =>
  normalizeChannelResponse(await endpoints.dashboard.getSalesByChannel(params, options), 'sales');

const fetchOrders: OrgMetricFetcher<DailyParams, DailyMetricRow[]> = async (endpoints, params, options) =>
  normalizeChannelResponse(await endpoints.dashboard.getOrderCountByChannel(params, options), 'orders');

const fetchStatuses: OrgMetricFetcher<StatusParams, DailyMetricRow[]> = async (endpoints, params, options) =>
  normalizeStatusResponse(await endpoints.dashboard.getChannelItemStatusBySubscription(params, options));

const readyTotals = (state: OrgMetricState<DailyMetricRow[]>): DailySeriesState => {
  if (state.status !== 'ready') return state;
  const window = orgDayRange({ timezone: state.timezone, days: DAILY_WINDOW_DAYS, now: state.requestedAt });
  return {
    ...state,
    window,
    series: buildDailyTotals({ rows: state.result, window, timezone: state.timezone }),
    latestObservedDay: null,
  };
};

const readySnapshots = (state: OrgMetricState<DailyMetricRow[]>): DailySeriesState => {
  if (state.status !== 'ready') return state;
  const window = orgDayRange({ timezone: state.timezone, days: DAILY_WINDOW_DAYS, now: state.requestedAt });
  const snapshot = buildDailySnapshots({ rows: state.result, window, timezone: state.timezone });
  return { ...state, window, ...snapshot };
};

export const useDailyChannelTotals = (metric: 'sales' | 'orders', failureMessage?: string): DailySeriesState => {
  const { timezone } = useOrgCalendar();
  const params = useMemo(() => ({ days: DAILY_WINDOW_DAYS, timezone }), [timezone]);
  const fetcher = metric === 'sales' ? fetchSales : fetchOrders;
  const defaultFailureMessage = metric === 'sales' ? 'Failed to load gross income by channel' : 'Failed to load order counts by channel';
  const state = useOrgMetric(fetcher, params, failureMessage ?? defaultFailureMessage);
  return useMemo(() => readyTotals(state), [state]);
};

export const useDailyItemStatusSnapshots = (subscriptionId: number): DailySeriesState => {
  const { timezone } = useOrgCalendar();
  const params = useMemo(() => ({ subscriptionId, days: DAILY_WINDOW_DAYS, timezone }), [subscriptionId, timezone]);
  const state = useOrgMetric(fetchStatuses, params, 'Failed to load item statuses');
  return useMemo(() => readySnapshots(state), [state]);
};
